<template>
  <div>
    <div class="row">
      <div class="col-12">
        <card class="custom-shadow-card-header">
          <div class="row">
            <select-component
              :rowClass="`col-12 col-md-12 col-lg-4`"
              :isMultiple="false"
              :dataOptions="businesses"
              :requireClass="`required-business-id`"
              @onChange="handleChangeSelected"
              @onClear="cleanAll()"
              :labelSelect="`${$t('common_words.company')}: *`"
              modelName="business_id"
              :initialSelection="LogPlannerForm.business_id"
            />

            <div class="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-2">
              <label>{{ $t('common_words.year') }}: *</label>
              <br />
              <el-date-picker
                v-if="renderComponent"
                type="year"
                class="shadow-sm required-year"
                v-model="LogPlannerForm.year_sel"
                value-format="yyyy"
                @change="handleChangePicker"
                :clearable="false"
                :picker-options="pickerOptions"
                :placeholder="`${$t('common_words.select2')}...`"
              />
            </div>

            <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
              <div class="row custom-mt-30">
                <div class="col-12 col-sm-6 col-md-4 mb-2">
                  <l-button
                    wide
                    type="info"
                    class="btn-filter"
                    @click="validateForm"
                    style="width: inherit"
                  >
                    {{ $t('common_words.generate') }}
                  </l-button>
                </div>
              </div>
            </div>
          </div>
        </card>
      </div>
    </div>

    <div v-if="tableGenerated" class="row">
      <div class="col-12">
        <card>
          <div
            class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
          >
            <div class="mt-2">
              <label class="mr-2"
                ><strong>{{ $t('grow.entry_mode') }}:</strong></label
              >
              <el-radio-group v-model="entryMode">
                <el-radio-button class="custom-radio-button" label="manual">
                  {{ $t('common_words.manual') }}
                </el-radio-button>

                <el-radio-button class="custom-radio-button" label="automatic">
                  {{ $t('common_words.automatic') }}
                </el-radio-button>
              </el-radio-group>
            </div>
          </div>

          <div
            class="custom-table-responsive table-responsive log-planner-table-wrapper"
          >
            <table class="table table-bordered table-journals not-last-td">
              <thead class="cusmtom-thead-color">
                <tr class="cusmtom-thead-color">
                  <th
                    scope="col"
                    class="text-center p-3 custom-header-label fixed-column-users-th"
                  >
                    {{ $t('common_words.users') }}
                  </th>
                  <th
                    scope="col"
                    class="text-center p-3 custom-header-label fixed-column-audit-th"
                  >
                    {{ $t('audit.audit') }}
                  </th>

                  <th
                    v-for="week in weeks"
                    :key="week.code"
                    scope="col"
                    class="text-center p-3 custom-header-label"
                    style="min-width: 100px"
                  >
                    {{ week.code }}
                  </th>

                  <th
                    scope="col"
                    class="text-center p-3 custom-header-label fixed-column-actions-th"
                    style="min-width: 100px"
                  >
                    {{ $t('common_words.actions') }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, rowIndex) in rows" :key="row.id">
                  <td class="text-center fixed-column-users-td">
                    <el-select
                      v-model="row.user_ids"
                      @change="handleUsersSelectAll(row)"
                      multiple
                      clearable
                      filterable
                      collapse-tags
                      size="large"
                      :placeholder="`${$t('common_words.select')}...`"
                      class="select-primary custom-width-select required-users shadow-sm"
                    >
                      <el-option
                        v-for="user in users"
                        class="select-primary selected-all"
                        :key="user.id"
                        :label="user.name"
                        :value="user.id"
                      />
                    </el-select>
                  </td>

                  <td class="text-center fixed-column-audit-td">
                    <el-select
                      v-model="row.audit2_heade_id"
                      clearable
                      filterable
                      size="large"
                      :placeholder="`${$t('common_words.select')}...`"
                      class="select-primary custom-width-select required-audit shadow-sm"
                    >
                      <el-option
                        v-for="audit in audit2Heades"
                        class="select-primary"
                        :key="audit.id"
                        :label="audit.name"
                        :value="audit.id"
                      />
                    </el-select>
                  </td>

                  <td
                    v-for="week in weeks"
                    :key="`${row.id}-${week.code}`"
                    class="text-center align-middle"
                  >
                    <div>
                      <el-checkbox
                        v-model="row.weeks[week.code].checked"
                        :disabled="week.disabled"
                        @change="handleWeekCheck(rowIndex, week.code, $event)"
                      />
                    </div>

                    <div
                      v-if="row.weeks[week.code].checked"
                      class="mt-1 small text-muted"
                    >
                      {{
                        weekDays
                          .filter((day) => row.weeks[week.code].days[day.value])
                          .map((day) => day.label)
                          .join(', ')
                      }}
                    </div>
                  </td>

                  <td class="text-center align-middle fixed-column-actions-td">
                    <a
                      v-if="hasCheckedOptions(rowIndex)"
                      v-tooltip.top-center="$t('common_words.clear_checks')"
                      class="btn-warning btn-simple btn-link custom-margin-actions"
                      @click="clearRowSelections(rowIndex)"
                    >
                      <em class="fa fa-history custom-size-fa-actions"></em>
                    </a>

                    <a
                      v-tooltip.top-center="$t('common_words.add')"
                      class="btn-success btn-simple btn-link custom-margin-actions"
                      @click="addRow"
                    >
                      <em class="fa fa-plus custom-size-fa-actions"></em>
                    </a>

                    <a
                      v-if="rows.length > 1"
                      v-tooltip.top-center="$t('common_words.erase')"
                      class="btn-danger btn-simple btn-link custom-margin-actions"
                      @click="removeRow(rowIndex)"
                    >
                      <em class="fa fa-times custom-size-fa-actions"></em>
                    </a>
                  </td>
                </tr>

                <tr v-if="rows.length === 0">
                  <td :colspan="weeks.length + 3" class="text-center">
                    {{ $t('common_words.no_records_found') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="row">
            <div class="col-6 text-right" v-if="isCreate() || isUpdate()">
              <l-button
                wide
                type="info"
                class="btn-filter m-4"
                @click="savePlanner"
              >
                {{ $t('common_words.save') }}
              </l-button>
            </div>
            <div class="col-6 text-left" v-if="isDelete()">
              <l-button
                wide
                type="danger"
                class="btn-filter m-4"
                @click="deletePlanner"
              >
                {{ $t('common_words.delete') }}
              </l-button>
            </div>
          </div>
        </card>
      </div>
    </div>

    <el-dialog
      :title="
        entryMode === 'automatic'
          ? $t('labor.enter_periodicity')
          : $t('grow.days_to_plan')
      "
      :visible.sync="automaticModal.visible"
      width="20%"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
    >
      <div v-if="entryMode === 'automatic'">
        <label>{{ $t('grow.how_many_weeks') }}:</label>

        <fg-input
          min="1"
          type="number"
          step="0"
          class="m-2 text-center"
          v-model="automaticModal.periodicity"
          :name="`labor.periodicity`"
        />
      </div>

      <div class="mt-3">
        <label>{{ $t('grow.days_to_plan') }}:</label>

        <div class="mt-2">
          <div
            v-for="day in weekDays"
            :key="`automatic-day-${day.value}`"
            class="text-left"
          >
            <el-checkbox v-model="automaticModal.days[day.value]">
              {{ day.label }}
            </el-checkbox>
          </div>
        </div>
      </div>

      <hr />

      <div class="text-right mt-2">
        <span class="dialog-footer">
          <button
            class="btn btn-danger custom-shadow-btn"
            @click="closeAutomaticModal"
          >
            {{ $t('common_words.cancel') }}</button
          >&nbsp;
          <button
            class="btn custom-btn-success custom-shadow-btn"
            @click="applyAutomaticPeriodicity"
          >
            {{ $t('common_words.confirm') }}
          </button>
        </span>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import { Axios } from 'src/class/Axios.js';
import { isset } from 'src/util/Util';
import { RadioGroup, Radio, RadioButton } from 'element-ui';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinMenu from 'src/util/mixinMenu';
import mixinMasters from 'src/util/mixinMasters';
import MixinLoading from 'src/mixins/mixin-loading';
import MixinPermissions from 'src/util/mixinPermissions';
import MixinValidations from 'src/mixins/forms/mixin-validations';
import SweetAlert from 'src/class/Alerts';
import i18n from 'src/i18n';

export default {
  name: 'base-planner',

  components: {
    [RadioGroup.name]: RadioGroup,
    [Radio.name]: Radio,
    [RadioButton.name]: RadioButton,
  },
  mixins: [
    MixinForm,
    MixinValidations,
    MixinPermissions,
    MixinMenu,
    mixinMasters,
    MixinLoading,
  ],

  props: {
    // Objeto con las rutas de la API específicas para el módulo
    apiEndpoints: {
      type: Object,
      required: true,
    },
    // Llave del menú para marcarlo como activo
    menuKey: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      disabledField: false,
      tableGenerated: false,
      entryMode: 'automatic',
      user_id: this.$store.state.auth.user.id,

      LogPlannerForm: {
        year_sel: null,
        business_id: null,
      },

      businesses: [],
      users: [],
      audit2Heades: [],
      rows: [],
      weeks: [],

      automaticModal: {
        visible: false,
        rowIndex: null,
        weekCode: null,
        periodicity: 1,
        days: {},
      },

      has_details: false,
      log_planner_header_id: null,

      pickerOptions: {
        disabledDate(time) {
          const currentYear = new Date().getFullYear();
          const selectedYear = new Date(time).getFullYear();

          return selectedYear < currentYear;
        },
      },
    };
  },

  computed: {
    weekDays() {
      return [
        { value: 1, label: i18n.t('grow.monday') },
        { value: 2, label: i18n.t('grow.tuesday') },
        { value: 3, label: i18n.t('grow.wednesday') },
        { value: 4, label: i18n.t('grow.thursday') },
        { value: 5, label: i18n.t('grow.friday') },
        { value: 6, label: i18n.t('grow.saturday') },
        { value: 7, label: i18n.t('grow.sunday') },
      ];
    },
  },

  watch: {
    '$i18n.locale': 'forceRerender',
  },

  methods: {
    validateForm() {
      if (!isset(this.LogPlannerForm.business_id)) {
        return this.validationInputsRequired('business-id');
      } else if (!isset(this.LogPlannerForm.year_sel)) {
        return this.validationInputsRequired('year');
      } else {
        this.loadPlannerTable();
      }
    },

    handleUsersSelectAll(row) {
      if (!row.user_ids || !row.user_ids.includes(0)) {
        return;
      }

      row.user_ids = this.users
        .map((item) => item.id)
        .filter((item) => Number(item) !== 0);
    },

    handleChangePicker() {
      this.cleanAll();
    },

    handleChangeSelected(data) {
      const previousValue = this.LogPlannerForm[data.name];
      this.$set(this.LogPlannerForm, data.name, data.selects);

      if (data.name === 'business_id' && previousValue !== data.selects) {
        this.cleanAll();
      }
    },

    async loadPlannerTable() {
      this.$emit('showHideLoading', true);

      try {
        let business_id = this.LogPlannerForm.business_id;
        let year = parseInt(this.LogPlannerForm.year_sel);
        this.has_details = false;
        this.log_planner_header_id = null;
        this.entryMode = 'automatic';
        const response = await Axios.getMasters(
          `${this.apiEndpoints.catchs}/${year}/${business_id}`
        );

        this.weeks = this.buildWeeksOfYear(
          parseInt(this.LogPlannerForm.year_sel)
        );

        if (response.data.data && response.data.data.rows.length) {
          this.rows = response.data.data.rows;
          this.has_details = true;
          this.log_planner_header_id = response.data.data.header_id;
          this.rebuildRowsWeekStructure();
        } else {
          this.rows = [this.createEmptyRow()];
        }

        this.tableGenerated = true;
        this.$emit('showHideLoading', false);
      } catch (error) {
        this.tableGenerated = false;
        this.$emit('showHideLoading', false);
        SweetAlert.warning(error.response.data.message);
      }
    },

    /**
     * Crea una nueva fila vacía para el planner.
     * Cada semana contiene:
     * - checked: false
     * - days: objeto con los días de la semana en false
     *
     * @returns {Object} Objeto fila con estructura completa inicializada
     */
    createEmptyRow() {
      const weekStructure = {};

      this.weeks.forEach((week) => {
        weekStructure[week.code] = {
          checked: false,
          days: this.createEmptyDays(),
        };
      });

      return {
        id: `row_${Date.now()}_${Math.random()}`,
        user_ids: [],
        audit2_heade_id: null,
        weeks: weekStructure,
      };
    },

    createEmptyDays() {
      return {
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
        6: false,
        7: false,
      };
    },

    /**
     * Reconstruye la estructura de semanas para cada fila existente.
     * - Asegurar que todas las semanas del año estén presentes
     * - Normalizar valores booleanos (checked y days)
     * - Evitar inconsistencias si faltan semanas en la respuesta
     *
     * @returns {void}
     */
    rebuildRowsWeekStructure() {
      this.rows = this.rows.map((row) => {
        const weekStructure = {};

        this.weeks.forEach((week) => {
          if (row.weeks && row.weeks[week.code]) {
            weekStructure[week.code] = {
              checked: !!row.weeks[week.code].checked,
              days: {
                1: !!row.weeks[week.code].days[1],
                2: !!row.weeks[week.code].days[2],
                3: !!row.weeks[week.code].days[3],
                4: !!row.weeks[week.code].days[4],
                5: !!row.weeks[week.code].days[5],
                6: !!row.weeks[week.code].days[6],
                7: !!row.weeks[week.code].days[7],
              },
            };
          } else {
            weekStructure[week.code] = {
              checked: false,
              days: this.createEmptyDays(),
            };
          }
        });

        return {
          ...row,
          weeks: weekStructure,
        };
      });
    },

    addRow() {
      this.rows.push(this.createEmptyRow());
    },

    removeRow(rowIndex) {
      if (this.rows.length === 1) {
        this.clearRowSelections(rowIndex);
        this.rows[rowIndex].user_ids = [];
        this.rows[rowIndex].audit2_heade_id = null;

        return;
      }

      this.rows.splice(rowIndex, 1);
    },

    clearRowSelections(rowIndex) {
      const row = this.rows[rowIndex];

      Object.keys(row.weeks).forEach((weekCode) => {
        row.weeks[weekCode].checked = false;
        row.weeks[weekCode].days = this.createEmptyDays();
      });
    },

    clearFutureWeeksFrom(rowIndex, startIndex) {
      for (let i = startIndex; i < this.weeks.length; i++) {
        const currentWeek = this.weeks[i];
        this.rows[rowIndex].weeks[currentWeek.code].checked = false;
        this.rows[rowIndex].weeks[currentWeek.code].days =
          this.createEmptyDays();
      }
    },

    /**
     * Maneja el evento de selección/deselección de una semana.
     *
     * @param {Number} rowIndex - Índice de la fila en el arreglo
     * @param {String} weekCode - Código de la semana (ej: 202501)
     * @param {Boolean} checked - Estado del checkbox
     *
     * @returns {void}
     */
    handleWeekCheck(rowIndex, weekCode, checked) {
      const row = this.rows[rowIndex];

      if (!checked) {
        row.weeks[weekCode].checked = false;
        row.weeks[weekCode].days = this.createEmptyDays();

        return;
      }

      this.automaticModal.visible = true;
      this.automaticModal.rowIndex = rowIndex;
      this.automaticModal.weekCode = weekCode;
      this.automaticModal.periodicity = 1;
      this.automaticModal.days = this.createEmptyDays();
    },

    closeAutomaticModal() {
      const { rowIndex, weekCode } = this.automaticModal;

      if (rowIndex !== null && weekCode) {
        this.rows[rowIndex].weeks[weekCode].checked = false;
      }

      this.automaticModal.visible = false;
      this.automaticModal.rowIndex = null;
      this.automaticModal.weekCode = null;
      this.automaticModal.periodicity = 1;
      this.automaticModal.days = this.createEmptyDays();
    },

    /**
     * Aplica la lógica de planificación automática por periodicidad.
     *
     * - Valida que exista:
     *   fila válida, semana inicial, periodicidad >= 1, al menos un día seleccionado
     *
     * - A partir de la semana seleccionada:
     *   - Limpia semanas futuras, Marca semanas según el salto definido (periodicity)
     *   - Asigna los mismos días seleccionados a cada semana
     *
     * @returns {void}
     */
    applyAutomaticPeriodicity() {
      const { rowIndex, weekCode, periodicity, days } = this.automaticModal;

      if (rowIndex === null || !weekCode) {
        return;
      }

      if (
        this.entryMode === 'automatic' &&
        (!periodicity || parseInt(periodicity) < 1)
      ) {
        return;
      }

      const hasSelectedDays = Object.values(days).some((value) => value);

      if (!hasSelectedDays) {
        return SweetAlert.warning(i18n.t('grow.select_at_least_one_day'));
      }

      const startIndex = this.weeks.findIndex((item) => item.code === weekCode);

      if (startIndex === -1) {
        return;
      }

      if (this.entryMode === 'automatic') {
        const step = parseInt(periodicity);
        this.clearFutureWeeksFrom(rowIndex, startIndex);

        for (let i = startIndex; i < this.weeks.length; i += step) {
          const currentWeek = this.weeks[i];
          this.rows[rowIndex].weeks[currentWeek.code].checked = true;
          this.rows[rowIndex].weeks[currentWeek.code].days = { ...days };
        }
      } else {
        // Modo Manual: Aplicar solo a la semana actual seleccionada
        this.rows[rowIndex].weeks[weekCode].checked = true;
        this.rows[rowIndex].weeks[weekCode].days = { ...days };
      }

      // Cerrar y limpiar modal
      this.automaticModal.visible = false;
      this.automaticModal.rowIndex = null;
      this.automaticModal.weekCode = null;
      this.automaticModal.periodicity = 1;
      this.automaticModal.days = this.createEmptyDays();
    },

    hasCheckedOptions(rowIndex) {
      const row = this.rows[rowIndex];

      return Object.keys(row.weeks).some((weekCode) => {
        const week = row.weeks[weekCode];

        if (week.checked) {
          return true;
        }

        return Object.values(week.days).some((dayChecked) => dayChecked);
      });
    },

    /**
     * Construye la lista de semanas ISO de un año específico.
     *
     * @param {Number} year - Año seleccionado
     * @returns {Array<Object>} Lista de semanas del año
     */
    buildWeeksOfYear(year) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentWeek = this.getISOWeek(currentDate);
      const totalWeeks = this.getISOWeeksInYear(year);
      const weeks = [];

      for (let week = 1; week <= totalWeeks; week++) {
        const paddedWeek = String(week).padStart(2, '0');
        const code = `${year}${paddedWeek}`;

        weeks.push({
          code,
          week,
          year,
          disabled: year === currentYear && week < currentWeek,
        });
      }

      return weeks;
    },

    /**
     * Calcula el número de semana ISO (ISO-8601) para una fecha dada.
     *
     * @param {Date} date - Fecha a evaluar
     * @returns {Number} Número de semana ISO
     */
    getISOWeek(date) {
      const tempDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      const dayNum = tempDate.getUTCDay() || 7;
      tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));

      return Math.ceil(((tempDate - yearStart) / 86400000 + 1) / 7);
    },

    getISOWeeksInYear(year) {
      const d = new Date(year, 11, 31);
      const week = this.getISOWeek(d);

      return week === 1 ? 52 : week;
    },

    /**
     * Normaliza la información del planner antes de enviarla al backend.
     * Procesa y Filtra únicamente filas válidas
     *
     * @returns {Object} Payload listo para envío al backend
     */
    normalizePayload() {
      const normalizedRows = this.rows
        .map((row) => {
          const selectedWeeks = Object.keys(row.weeks)
            .filter((weekCode) => row.weeks[weekCode].checked)
            .map((weekCode) => {
              const days = Object.keys(row.weeks[weekCode].days)
                .filter((day) => row.weeks[weekCode].days[day])
                .map((day) => parseInt(day));

              return {
                year_week: weekCode,
                days,
              };
            });

          return {
            audit2_heade_id: row.audit2_heade_id,
            user_ids: row.user_ids,
            weeks: selectedWeeks,
          };
        })
        .filter((row) => {
          return (
            row.audit2_heade_id &&
            row.user_ids &&
            row.user_ids.length > 0 &&
            row.weeks.length > 0
          );
        });

      return {
        route: this.apiEndpoints.store,
        business_id: this.LogPlannerForm.business_id,
        year: parseInt(this.LogPlannerForm.year_sel),
        user_id: this.user_id,
        rows: normalizedRows,
      };
    },

    async savePlanner() {
      const payload = this.normalizePayload();

      if (!payload.rows.length) {
        return SweetAlert.warning(i18n.t('grow.add_a_valid_row'));
      }

      this.$emit('showHideLoading', true);

      await Axios.postMasters(payload)
        .then((response) => {
          this.$emit('showHideLoading', false);
          this.cleanAll();
          SweetAlert.success(response.data.message);
        })
        .catch((error) => {
          this.$emit('showHideLoading', false);

          const data = error.response.data;
          let messageError = 'Ocurrió un error al guardar.';

          if (data) {
            if (data.error) {
              messageError = data.error;
            } else if (data.errors) {
              const firstError = Object.values(data.errors)[0][0];
              messageError = firstError;
            } else if (data.message) {
              messageError = data.message;
            }
          }
          this.showMessageErrors(messageError);
        });
    },

    deletePlanner() {
      if (this.has_details) {
        SweetAlert.delete(i18n.t('production.capture_details')).then(
          (result) => {
            if (result.value) {
              this.$emit('showHideLoading', true);
              let data = {
                id: this.log_planner_header_id,
                route: this.apiEndpoints.delete,
              };
              Axios.destroyMaster(data)
                .then((response) => {
                  this.$emit('showHideLoading', false);
                  this.cleanAll();
                  SweetAlert.success(response.data.message);
                })
                .catch((error) => {
                  this.$emit('showHideLoading', false);
                  SweetAlert.warning(error);
                });
            }
          }
        );
      } else {
        SweetAlert.warning(i18n.t('production.no_record_to_delete'));
      }
    },

    cleanAll() {
      this.tableGenerated = false;
      this.weeks = [];
      this.rows = [];
      this.has_details = false;
      this.log_planner_header_id = null;

      this.automaticModal = {
        visible: false,
        rowIndex: null,
        weekCode: null,
        periodicity: 1,
        days: this.createEmptyDays(),
      };
    },
  },

  async created() {
    this.$emit('showHideLoading', true);

    Promise.all([
      Axios.getMasters('businesses/only'),
      Axios.getMasters('users/indexfield'),
      Axios.getMasters(this.apiEndpoints.inspections),
    ])
      .then(([responseBusinesses, usersResponse, responseAudit]) => {
        this.businesses = responseBusinesses.data.data;
        this.users = this.concatAll(usersResponse.data.data);
        this.audit2Heades = responseAudit.data.data;
        this.$emit('showHideLoading', false);
      })
      .catch((error) => {
        this.$emit('showHideLoading', false);
        SweetAlert.warning(error.response.data.message);
      });
  },

  mounted() {
    this.selectedItemOnMenu(this.menuKey);
  },

  destroyed() {
    this.deselectedItemOnMenu(this.menuKey);
  },
};
</script>
<style scoped>
.log-planner-table-wrapper {
  max-height: 70vh;
  overflow: auto;
  position: relative;
}

/* Header completo fijo al hacer scroll vertical */
.log-planner-table-wrapper thead th {
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: #898989;
}

/* Columnas laterales fijas */
.fixed-column-users-th,
.fixed-column-audit-th,
.fixed-column-actions-th,
.fixed-column-users-td,
.fixed-column-audit-td,
.fixed-column-actions-td {
  position: sticky;
  border-right: 1px solid #dee2e6;
  background-clip: padding-box;
}

/* Header de columnas fijas */
.fixed-column-users-th,
.fixed-column-audit-th,
.fixed-column-actions-th {
  background-color: #898989;
  z-index: 7 !important;
}

/* Celdas del body de columnas fijas */
.fixed-column-users-td,
.fixed-column-audit-td,
.fixed-column-actions-td {
  background-color: #fff;
  z-index: 3;
}

/* Posición horizontal */
.fixed-column-users-th,
.fixed-column-users-td {
  left: 0;
  min-width: 270px;
}

.fixed-column-audit-th,
.fixed-column-audit-td {
  left: 265px;
  min-width: 250px;
}

.fixed-column-actions-th,
.fixed-column-actions-td {
  right: 0;
  min-width: 100px;
}

/* Intersección header + sticky columns */
.fixed-column-users-th,
.fixed-column-audit-th,
.fixed-column-actions-th {
  top: 0;
}

/* Sombras para separar visualmente */
.fixed-column-users-th,
.fixed-column-users-td,
.fixed-column-audit-th,
.fixed-column-audit-td {
  box-shadow: 1px 0 0 #dee2e6;
}

.fixed-column-actions-th,
.fixed-column-actions-td {
  box-shadow: -1px 0 0 #dee2e6;
}
</style>
